import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import { useSocket } from '../../context/SocketContext';
import { FiHeart, FiMessageSquare, FiCalendar, FiMapPin, FiBriefcase, FiUser, FiEye, FiUsers, FiAlertTriangle, FiSlash } from 'react-icons/fi';
import { useToast } from '../../context/ToastContext';
import MessageModal from '../../components/dashboard/MessageModal.js';
import ReportModal from '../../components/dashboard/ReportModal.js';
import ConfirmationModal from '../../components/admin/ConfirmationModal';
import { buildApiUrl } from '../../config/api';
import { fetchJson, formatFetchError } from '../../utils/fetchJson';
import { getProfilePhotoUrl } from '../../utils/profileUtils';
import { countries } from '../../data/selectOptions';

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const { onlineUsers } = useSocket();
  const [profileUser, setProfileUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [messageModal, setMessageModal] = useState({
    isOpen: false,
    recipient: null
  });
  const { success, error: toastError } = useToast();
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'danger'
  });

  const fetchUserProfile = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl(`/api/user-profiles/${userId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && data.success) {
        setProfileUser(data.user);
        // Use server-provided isLiked status which is more reliable
        setIsLiked(data.user.isLiked !== undefined ? data.user.isLiked : (data.user.likedBy?.includes(currentUser?.id) || false));
        console.log('👤 User profile fetched:', data.user);
      } else {
        setError(data.message || 'Failed to fetch user profile');
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError(formatFetchError(err, 'Failed to fetch user profile. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [userId, currentUser?.id]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  const handleLikeProfile = async () => {
    if (isProcessing || !profileUser) return;

    // Don't allow liking own profile
    if (profileUser._id === currentUser?.id) {
      setError('You cannot like your own profile');
      return;
    }

    // Optimistic UI update - show instant feedback
    const wasLiked = isLiked;
    setIsLiked(!wasLiked);
    setIsProcessing(true);

    // Show instant feedback to user
    console.log(wasLiked ? '💔 Profile unliked (instant)' : '❤️ Profile liked (instant)');

    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl(`/api/user-profiles/${userId}/like`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && data.success) {
        console.log('✅ Server confirmed like action');

        setProfileUser(prev => ({
          ...prev,
          likedBy: !wasLiked
            ? [...(prev.likedBy || []), currentUser?.id]
            : prev.likedBy?.filter(id => id !== currentUser?.id) || []
        }));
      } else {
        setIsLiked(wasLiked);
        setError(data.message || 'Failed to update like status');
        console.error('Server rejected like action:', data.message);
      }
    } catch (err) {
      setIsLiked(wasLiked);
      setError(formatFetchError(err, 'Failed to update like status. Please try again.'));
      console.error('Error liking profile:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMessageUser = () => {
    // Open message modal instead of navigating - same as MyMatches
    setMessageModal({
      isOpen: true,
      recipient: profileUser
    });
  };

  const handleCloseMessageModal = () => {
    setMessageModal({
      isOpen: false,
      recipient: null
    });
  };

  const handleSendMessage = (newMessage) => {
    // This will be called when a message is sent successfully
    console.log('📨 Message sent:', newMessage);
  };

  const handleReportUser = () => {
    setReportModalOpen(true);
  };

  const handleSubmitReport = async (reportData) => {
    setIsReporting(true);
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl(`/api/user-profiles/${userId}/report`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reportData)
      });

      if (response.ok && data.success) {
        setReportModalOpen(false);
        success('Report submitted successfully. Thank you for helping keep our community safe.');
      } else {
        toastError(data.message || 'Failed to submit report. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      toastError('Network error. Please try again.');
    } finally {
      setIsReporting(false);
    }
  };



  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatLocation = (countryCode, city) => {
    const countryObj = countries.find(c => c.value === countryCode);
    const countryName = countryObj ? countryObj.label : countryCode;

    if (city && countryName) return `${city}, ${countryName}`;
    if (countryName) return countryName;
    if (city) return city;
    return 'Location not specified';
  };

  const handleGoBack = () => {
    const from = location.state?.from;
    if (typeof from === 'string' && from.trim().length > 0) {
      navigate(from);
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/dashboard/profiles');
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center space-x-6 mb-8">
              <div className="w-32 h-32 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-8 bg-gray-200 rounded mb-4 w-64"></div>
                <div className="h-4 bg-gray-200 rounded mb-2 w-48"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-700">{error}</span>
            <button
              onClick={fetchUserProfile}
              className="ml-auto text-red-600 hover:text-red-800 font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <div className="text-6xl mb-4 text-romantic-400"><FiUser /></div>
        <h3 className="text-xl font-semibold mb-2 text-gray-900">Profile not found</h3>
        <p className="text-gray-600 mb-6">The user profile you're looking for doesn't exist.</p>
        <button
          onClick={handleGoBack}
          className="bg-romantic-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-romantic-600 transition-colors"
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Cover Photo Section */}
          <div className="relative h-48 bg-gradient-to-r from-romantic-400 to-primary-400">
            <button
              onClick={handleGoBack}
              className="absolute top-4 left-4 bg-white/90 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-white transition-colors"
            >
              ← Back
            </button>
          </div>

          {/* Profile Info */}
          <div className="px-4 md:px-8 pb-8">
            <div className="relative z-10 flex flex-col md:flex-row items-center md:items-end -mt-16 mb-8">
              <div className="relative shrink-0">
                <img
                  src={getProfilePhotoUrl(profileUser)}
                  alt={`${profileUser.firstName} ${profileUser.lastName}`}
                  className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                />
                {onlineUsers?.has((profileUser._id || profileUser.id)?.toString()) && (
                  <div className="absolute bottom-2 right-2 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                )}
              </div>

              <div className="mt-4 md:mt-0 md:ml-6 flex-1 w-full">
                <div className="flex flex-col md:flex-row items-center md:justify-between gap-4">
                  <div className="text-center md:text-left w-full md:w-auto">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                      {profileUser.firstName} {profileUser.lastName}
                    </h1>
                    <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-gray-900 border border-blue-100">
                        <FiCalendar className="mr-1.5 w-4 h-4 text-blue-500" />
                        {calculateAge(profileUser.dateOfBirth)} years
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-50 text-gray-900 border border-red-100">
                        <FiMapPin className="mr-1.5 w-4 h-4 text-red-500" />
                        <span className="capitalize">{formatLocation(profileUser.country, profileUser.city)}</span>
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-50 text-gray-900 border border-purple-100">
                        <FiBriefcase className="mr-1.5 w-4 h-4 text-purple-500" />
                        <span className="capitalize">{profileUser.profession || 'Not specified'}</span>
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap justify-center md:justify-end gap-2 w-full md:w-auto mt-4 md:mt-0">
                    {/* Only show like button if not viewing own profile */}
                    {profileUser._id !== currentUser?.id && (
                      <button
                        onClick={handleLikeProfile}
                        disabled={isProcessing}
                        className={`flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all flex-1 md:flex-none whitespace-nowrap ${isLiked
                          ? 'bg-red-500 text-white hover:bg-red-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <FiHeart className={isLiked ? 'fill-current' : ''} />
                        <span>{isLiked ? 'Liked' : 'Like'}</span>
                      </button>
                    )}

                    <button
                      onClick={handleMessageUser}
                      className="flex items-center justify-center space-x-2 bg-romantic-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-romantic-600 transition-colors flex-1 md:flex-none whitespace-nowrap"
                    >
                      <FiMessageSquare />
                      <span>{profileUser._id === currentUser?.id ? 'Edit Profile' : 'Message'}</span>
                    </button>

                    {profileUser._id !== currentUser?.id && (
                      <div className="flex gap-2">
                        <button
                          onClick={handleReportUser}
                          className="p-3 bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 hover:text-red-600 transition-colors shrink-0"
                          title="Report User"
                        >
                          <FiAlertTriangle size={20} />
                        </button>
                        <button
                          onClick={() => {
                            setConfirmationModal({
                              isOpen: true,
                              title: 'Block User',
                              message: 'Are you sure you want to block this user? They will not be able to message you or see your profile.',
                              confirmText: 'Block User',
                              type: 'danger',
                              onConfirm: async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  await fetchJson(buildApiUrl(`/api/user-profiles/${userId}/block`), {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  success('User blocked successfully');
                                  setConfirmationModal(prev => ({ ...prev, isOpen: false }));
                                  navigate('/dashboard/profiles');
                                } catch (err) {
                                  toastError('Failed to block user');
                                  setConfirmationModal(prev => ({ ...prev, isOpen: false }));
                                }
                              }
                            });
                          }}
                          className="p-3 bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 hover:text-red-600 transition-colors shrink-0"
                          title="Block User"
                        >
                          <FiSlash size={20} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* About Section */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed">
                  {profileUser.aboutMe || 'No information provided yet.'}
                </p>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Age:</span>
                    <span className="font-medium">{calculateAge(profileUser.dateOfBirth)} years</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gender:</span>
                    <span className="font-medium capitalize">{profileUser.gender}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Religion:</span>
                    <span className="font-medium">{profileUser.religion || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Community:</span>
                    <span className="font-medium">{profileUser.community || 'Not specified'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Professional Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Education:</span>
                    <span className="font-medium">{profileUser.education || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Profession:</span>
                    <span className="font-medium">{profileUser.profession || 'Not specified'}</span>
                  </div>

                </div>
              </div>
            </div>

            {/* Interests */}
            {profileUser.interests && profileUser.interests.length > 0 && (
              <div className="mb-8">
                <h3 className="font-semibold text-gray-900 mb-4">Interests</h3>
                <div className="flex flex-wrap gap-2">
                  {profileUser.interests.map((interest, index) => (
                    <span
                      key={index}
                      className="bg-romantic-100 text-romantic-700 px-3 py-1 rounded-full text-sm"
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Activity Stats */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Profile Activity</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="flex justify-center text-2xl font-bold text-romantic-600 mb-1">
                    <FiEye />
                  </div>
                  <div className="text-2xl font-bold text-romantic-600">
                    {profileUser.profileViews?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Profile Views</div>
                </div>
                <div>
                  <div className="flex justify-center text-2xl font-bold text-romantic-600 mb-1">
                    <FiHeart className="fill-current" />
                  </div>
                  <div className="text-2xl font-bold text-romantic-600">
                    {profileUser.likedBy?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Likes</div>
                </div>
                <div>
                  <div className="flex justify-center text-2xl font-bold text-romantic-600 mb-1">
                    <FiUsers />
                  </div>
                  <div className="text-2xl font-bold text-romantic-600">
                    {profileUser.matches?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Matches</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Message Modal */}
      <MessageModal
        isOpen={messageModal.isOpen}
        onClose={handleCloseMessageModal}
        recipient={messageModal.recipient}
        currentUser={currentUser}
        onSendMessage={handleSendMessage}
      />

      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        userName={`${profileUser?.firstName} ${profileUser?.lastName}`}
        onSubmit={handleSubmitReport}
        isSubmitting={isReporting}
      />

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onConfirm={confirmationModal.onConfirm}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        type={confirmationModal.type}
      />
    </>
  );
};

export default UserProfile;
